import React, { useEffect, useState, useMemo, useRef } from 'react';
import ModalWrapper from './ModalWrapper';
import signsealLogo from './signseal_logo.png';
import './App.css';
import EditStockModal from './EditStockModal';
import CheckInOutModal from './CheckInOutModal';
import StockItemActionModal from './StockItemActionModal';
import BookingModal from './BookingModal';
import BelowMinBarChart from './BelowMinBarChart';
import OverRunsPage from './pages/OverRunsPage';
import StockManagementPage from './pages/StockManagementPage';
import CustomerManager from './CustomerManager';
import AddStockModal from './AddStockModal';
import BookingPage from './BookingPage';
import LoginModal from './LoginModal';
import UserManagement from './UserManagement';
import BookingsNotification from './BookingsNotification';
import ReportsPage from './pages/ReportsPage';
import BackordersPage from './pages/BackordersPage';
import CallOffsPage from './pages/CallOffsPage';
import CallOffModal from './CallOffModal';
import RawMaterialsPage from './pages/RawMaterialsPage';
import SilkscreensPage from './pages/SilkscreensPage';
import SilkscreenReportsPage from './pages/SilkscreenReportsPage';
import InvoicingPage from './pages/InvoicingPage';
import PublicSurveyPage from './pages/PublicSurveyPage';
import SurveyModule from './pages/SurveyModule';
import ProductionPage from './pages/ProductionPage';
import CompletedProductionPage from './pages/CompletedProductionPage';
import OperatorManager from './pages/OperatorManager';
import { stockItemsToCSV, csvToStockItems } from './StockCSVUtils';

const API_URL = process.env.REACT_APP_API_URL || '';

function App() {
  // --- Audit Log Date Filter State ---
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');
  const fileInputRef = useRef(null);

  // Export all items to CSV and trigger download
  function handleExportCSV() {
    // Pass customers array to include customerName in export
    const csv = stockItemsToCSV(items, customers);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'stock_items.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Import items from CSV file
  async function handleImportCSV(e) {
    const file = e.target.files[0];
    if (!file) return;
    const text = await file.text();
    const importedItems = csvToStockItems(text);
    if (!importedItems.length) {
      alert('No valid items found in CSV.');
      return;
    }
    // Send to backend for bulk creation (implement /items/bulk if not present)
    try {
      const res = await fetch(`${API_URL}/items/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: importedItems })
      });
      if (!res.ok) throw new Error('Import failed');
      alert('Import successful!');
      // Optionally refresh items
      fetch(`${API_URL}/items`).then(res => res.json()).then(setItems);
    } catch (err) {
      alert('Import failed: ' + err.message);
    }
  }

  // Track pending bookings count for instant notification updates
  const [pendingBookingsCount, setPendingBookingsCount] = useState(0);

  // Track active backorders count for notification icon
  const [activeBackordersCount, setActiveBackordersCount] = useState(0);
  
  // Trigger to refresh BackordersPage when backorders are updated externally
  const [backorderRefreshTrigger, setBackorderRefreshTrigger] = useState(0);

  // Authentication state
  const [currentUser, setCurrentUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [loginModal, setLoginModal] = useState({ open: false });
  const [userManagementModal, setUserManagementModal] = useState({ open: false });


  // Modal state for new action modal
  const [actionModal, setActionModal] = useState({ open: false, item: null });
  // Dashboard customer selection state
  const [selectedDashboardCustomer, setSelectedDashboardCustomer] = useState('ALL');

  // --- Stock Items State ---
  const [items, setItems] = useState([]); // Only declare ONCE at the top
  
  // --- HeroChart Cache Management ---
  const [chartCacheKey, setChartCacheKey] = useState(Date.now());
  
  // Function to invalidate chart cache when data changes
  const invalidateChartCache = () => {
    setChartCacheKey(Date.now());
  };

  // Compute dashboardFilteredItems based on selection
  const dashboardFilteredItems = React.useMemo(() => {
    if (selectedDashboardCustomer === 'ALL') return items.slice().sort((a, b) => {
      const aDelta = Number(a.currentQty) - Number(a.minQty);
      const bDelta = Number(b.currentQty) - Number(b.minQty);
      if (a.currentQty < a.minQty && b.currentQty >= b.minQty) return -1;
      if (a.currentQty >= a.minQty && b.currentQty < b.minQty) return 1;
      return aDelta - bDelta;
    });
    if (!selectedDashboardCustomer) return [];
    const filtered = items.filter(i => i.clientId === selectedDashboardCustomer);
    return filtered.sort((a, b) => {
      const aDelta = Number(a.currentQty) - Number(a.minQty);
      const bDelta = Number(b.currentQty) - Number(b.minQty);
      if (a.currentQty < a.minQty && b.currentQty >= b.minQty) return -1;
      if (a.currentQty >= a.minQty && b.currentQty < b.minQty) return 1;
      return aDelta - bDelta;
    });
  }, [items, selectedDashboardCustomer]);

  // Function to get customer-specific options for AddStockModal dropdowns
  const getClientOptions = (type, clientId, selectedDescription = null) => {
    if (!clientId || !items.length) return [];
    
    // Filter items by customer
    const customerItems = items.filter(item => item.clientId === clientId);
    
    if (type === 'descriptions') {
      // Return unique mainCategory values for this customer
      const descriptions = [...new Set(
        customerItems
          .map(item => item.mainCategory)
          .filter(desc => desc && desc.trim() !== '')
      )];
      return descriptions.sort();
    }
    
    if (type === 'variants') {
      // Return unique variantDesc values for this customer + selected description
      if (!selectedDescription) return [];
      
      const variants = [...new Set(
        customerItems
          .filter(item => item.mainCategory === selectedDescription)
          .map(item => item.variantDesc)
          .filter(variant => variant && variant.trim() !== '')
      )];
      return variants.sort();
    }
    
    return [];
  };

  // Add Stock Item modal state
  const [showAddStockForm, setShowAddStockForm] = useState(false);
  // --- Edit/Delete/Audit Logic State ---
  const [editModal, setEditModal] = useState({ open: false, item: null });
  const [auditLog, setAuditLog] = useState([]);
  const [showAudit, setShowAudit] = useState(false);

  // --- Check In/Out Modal State ---
  const [checkModal, setCheckModal] = useState({ open: false, item: null, mode: 'in' });

  // --- Booking Modal State ---
  const [bookingModal, setBookingModal] = useState({ open: false, item: null });

  // --- Call-off Modal State ---
  const [callOffModal, setCallOffModal] = useState({ open: false, item: null });

  // Ensure check-in/out modal is always closed on mount (prevents stuck open after refresh)
  useEffect(() => {
    setCheckModal({ open: false, item: null, mode: 'in' });
  }, []);
  const [checkQty, setCheckQty] = useState('');
  const [checkNote, setCheckNote] = useState('');
  const [checkError, setCheckError] = useState('');
  const [submittingCheck, setSubmittingCheck] = useState(false);

  function openCheckModal(item, mode) {
    setCheckModal({ open: true, item, mode });
    setCheckQty('');
    setCheckNote('');
    setCheckError('');
  }

  function closeCheckModal() {
    setCheckModal({ open: false, item: null, mode: 'in' });
    setCheckQty('');
    setCheckNote('');
    setCheckError('');
  }

  function handleCheckSubmit(e) {
    e.preventDefault();
    setSubmittingCheck(true);
    setCheckError('');
    const qty = Number(checkQty);
    if (!qty || qty <= 0) {
      setCheckError('Quantity must be a positive number');
      setSubmittingCheck(false);
      return;
    }
    const url = `${process.env.REACT_APP_API_URL || ''}/items/${checkModal.item.id}/${checkModal.mode === 'in' ? 'checkin' : 'checkout'}`;
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ quantity: qty, note: checkNote })
    })
      .then(res => {
        if (!res.ok) return res.json().then(err => { throw new Error(err.error || 'Failed'); });
        return res.json();
      })
      .then(updated => {
        setItems(items => items.map(i => i.id === updated.id ? updated : i));
        invalidateChartCache(); // Invalidate chart cache when stock changes
        closeCheckModal();
      })
      .catch(err => {
        setCheckError(err.message);
      })
      .finally(() => setSubmittingCheck(false));
  }


  function handleDelete(item) {
    if (!window.confirm('Are you sure you want to delete this stock item?')) return;
    if (!window.confirm('This action is irreversible. Are you REALLY sure?')) return;
    if ((window.prompt('Type DELETE to confirm:') || '').toUpperCase().trim() !== 'DELETE') return;
    fetch(`${API_URL}/items/${item.id}`, { method: 'DELETE' })
      .then(res => {
        if (!res.ok) throw new Error('Failed to delete item');
        // Remove from UI
        setItems(items => items.filter(i => i.id !== item.id));
        invalidateChartCache(); // Invalidate chart cache when item deleted
      })
      .catch(err => alert('Error deleting item: ' + err.message));
  }

  function handleEdit(item) {
    setEditModal({ open: true, item });
  }

  function handleEditChange(e) {
    const { name, value } = e.target;
    setEditModal(m => ({ ...m, item: { ...m.item, [name]: value } }));
  }

  function handleEditSubmit(e) {
    e.preventDefault();
    fetch(`${API_URL}/items/${editModal.item.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...editModal.item,
        minQty: Number(editModal.item.minQty),
        clientId: editModal.item.clientId,
      })
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to update item');
        return res.json();
      })
      .then(updated => {
        setItems(items => items.map(i => i.id === updated.id ? updated : i));
        invalidateChartCache(); // Invalidate chart cache when item updated
        setEditModal({ open: false, item: null });
      })
      .catch(err => alert('Error updating item: ' + err.message));
  }

  function fetchAuditLog(startDate, endDate) {
    if (!startDate || !endDate) {
      // Do not fetch or alert; just require user to pick both dates.
      return;
    }
    let url = `${API_URL}/auditlog`;
    const params = [];
    if (startDate) params.push(`startDate=${encodeURIComponent(typeof startDate === 'string' ? startDate : startDate?.toISOString?.().slice(0,10) || '')}`);
    if (endDate) params.push(`endDate=${encodeURIComponent(typeof endDate === 'string' ? endDate : endDate?.toISOString?.().slice(0,10) || '')}`);
    if (params.length) url += '?' + params.join('&');
    fetch(url)
      .then(res => res.json())
      .then(data => {
        console.log('[AUDIT LOG FETCHED]', data);
        setAuditLog(data);
      })
      .catch(() => setAuditLog([]));
    setShowAudit(true);
  }

  function renderEditModal() {
    if (!editModal.open) return null;
    return (
      <EditStockModal
        item={editModal.item}
        setEditModal={setEditModal}
        setItems={setItems}
        getClientOptions={getClientOptions}
      />
    );
  }

  function handleOpenAuditLog() {
    setShowAudit(true);
    setAuditLog([]);
    setStartDate('');
    setEndDate('');
  }

  function renderAuditModal() {
    if (!showAudit) return null;

    function handleFilter() {
      fetchAuditLog(startDate, endDate);
    }
    function handleReset() {
      setStartDate('');
      setEndDate('');
    }

    return (
      <ModalWrapper open={showAudit} onClose={() => setShowAudit(false)} minWidth={600} maxWidth={900} zIndex={100}>
        <div style={{ width: '100%' }}>
  <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
    <label>Start Date: <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} /></label>
    <label>End Date: <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} /></label>
    <button onClick={handleFilter} style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #1976d2', background: '#1976d2', color: '#fff', marginLeft: 8 }}>Filter</button>
    <button onClick={handleReset} style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #888', background: '#fff', color: '#1976d2', marginLeft: 8 }}>Reset</button>
  </div>
  <div style={{ width: '100%', marginTop: 16, overflowX: 'auto', maxHeight: 420, minHeight: 120, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px #1976d211', padding: 0, display: 'block', overflowY: 'auto' }}>

          <table border="1" cellPadding="6" style={{ width: '100%', minWidth: 600 }}>
            <thead>
              <tr><th>Time</th><th>User</th><th>Action</th><th>Details</th></tr>
            </thead>
            <tbody>
              {(!startDate || !endDate) ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: '#888' }}>Please select a start and end date, then click Filter to view the audit log.</td></tr>
              ) : (Array.isArray(auditLog) && auditLog.length > 0 ? auditLog.map(log => (
                <tr key={log.id}>
                  <td>{new Date(log.createdAt).toLocaleString()}</td>
                  <td>{log.user ? log.user.name : log.userId}</td>
                  <td>{log.action}</td>
                  <td style={{ maxWidth: 320, whiteSpace: 'pre-wrap', fontSize: 12 }}>{typeof log.details === 'string' ? log.details : (log.details && typeof log.details === 'object' ? JSON.stringify(log.details) : String(log.details))}</td>
                </tr>
              )) : (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: '#888' }}>No audit log entries for the selected date range.</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </ModalWrapper>
    );
  }

  const [customers, setCustomers] = useState([]);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', contactInfo: '' });
  const [customerError, setCustomerError] = useState(null);
  const [overRunsActive, setOverRunsActive] = useState(false);
  const [overRunsClientFilter, setOverRunsClientFilter] = useState('ALL');

  // Reset client filter when Over Runs is deactivated
  useEffect(() => {
    if (!overRunsActive) setOverRunsClientFilter('ALL');
  }, [overRunsActive]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch customers (clients) on mount
  useEffect(() => {
    fetch(`${API_URL}/clients`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch customers');
        return res.json();
      })
      .then((data) => setCustomers(data))
      .catch((err) => setError('Failed to fetch customers: ' + err.message));
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/items`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch stock items');
        return res.json();
      })
      .then((data) => {
        setItems(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // Fetch initial booking count for notification
  useEffect(() => {
    fetch(`${API_URL}/bookings`)
      .then(res => res.json())
      .then(data => {
        // Count only pending bookings
        const pending = data.filter(b => String(b.status).toUpperCase() === 'PENDING');
        setPendingBookingsCount(pending.length);
      })
      .catch(() => {
        // If fetch fails, notification will handle its own fetching
        console.log('Initial booking count fetch failed, notification will handle independently');
      });
  }, []);

  // Function to refresh backorder count
  const refreshBackorderCount = () => {
    fetch(`${API_URL}/backorders`)
      .then(res => res.json())
      .then(data => {
        // Count active backorders (pending and partially_fulfilled)
        const active = data.filter(b => b.status === 'pending' || b.status === 'partially_fulfilled');
        setActiveBackordersCount(active.length);
        // Trigger BackordersPage refresh if it's currently active
        setBackorderRefreshTrigger(prev => prev + 1);
      })
      .catch(() => {
        console.log('Backorder count refresh failed');
      });
  };

  // Fetch initial backorder count for notification icon
  useEffect(() => {
    refreshBackorderCount();
  }, []);

  // Check for existing authentication on app startup
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userStr = localStorage.getItem('currentUser');
    
    console.log('App startup - checking auth:', { token: !!token, userStr: !!userStr });
    
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        console.log('Restoring auth state:', user);
        setCurrentUser(user);
        setAuthToken(token);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        // Invalid stored data, clear it
        localStorage.removeItem('authToken');
        localStorage.removeItem('currentUser');
      }
    }
  }, []);

  // Authentication functions
  const handleLoginSuccess = (user, token) => {
    setCurrentUser(user);
    setAuthToken(token);
  };

  const handleLogout = async () => {
    try {
      // Call logout endpoint if we have a token
      if (authToken) {
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
          },
        });
      }
    } catch (error) {
      console.log('Logout API call failed, but continuing with local logout');
    } finally {
      // Clear local state and storage
      setCurrentUser(null);
      setAuthToken(null);
      localStorage.removeItem('authToken');
      localStorage.removeItem('currentUser');
    }
  };



  const [descriptions, setDescriptions] = useState([]);
  const [variants, setVariants] = useState([]);

  const initialForm = {
    description: '',
    productCode: '',
    variant: '',
    barcode: '',
    labelSize: '',
    minQty: '',
    currentQty: '',
    clientId: '', // Add customer selection
    customDescription: '',
    customVariant: '',
  };

  const [form, setForm] = useState(initialForm);

  // When client changes, load their options
  useEffect(() => {
    if (form.clientId) {
      setDescriptions(getClientOptions('descriptions', form.clientId));
      setVariants(getClientOptions('variants', form.clientId));
    } else {
      setDescriptions([]);
      setVariants([]);
    }
    setForm(f => ({ ...f, description: '', variant: '', customDescription: '', customVariant: '' }));
  }, [form.clientId]);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  // Handle dropdown selection for Description/Variant
  const handleDescriptionChange = (e) => {
    setForm({ ...form, description: e.target.value, customDescription: '' });
  };
  const handleVariantChange = (e) => {
    setForm({ ...form, variant: e.target.value, customVariant: '' });
  };

  // For custom input
  const handleCustomDescription = (e) => {
    setForm({ ...form, customDescription: e.target.value });
  };
  const handleCustomVariant = (e) => {
    setForm({ ...form, customVariant: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    // Simple validation
    // Use custom if selected
    const descValue = form.description === 'new' ? form.customDescription : form.description;
    const variantValue = form.variant === 'new' ? form.customVariant : form.variant;
    if (!descValue || !form.productCode || !variantValue || !form.labelSize || !form.minQty || !form.currentQty || !form.clientId) {
      setFormError('Please fill in all required fields.');
      setSubmitting(false);
      return;
    }
    try {
      // Remove notes before sending to backend
      const res = await fetch(`${API_URL}/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mainCategory: descValue,
          productCode: form.productCode,
          variantDesc: variantValue,
          barcode: form.barcode,
          labelSize: form.labelSize,
          minQty: Number(form.minQty),
          currentQty: Number(form.currentQty),
          clientId: form.clientId,
        })
      });
      if (!res.ok) {
        let errMsg = 'Failed to create stock item';
        try {
          const errData = await res.json();
          errMsg = errData.error || errMsg;
          if (errData.details) {
            errMsg += ': ' + errData.details;
          }
        } catch {}
        setFormError(errMsg);
      } else {
        // Save new description/variant to localStorage for this client only
        let updatedDescriptions = [...descriptions];
        let updatedVariants = [...variants];
        if (form.clientId && descValue && !descriptions.includes(descValue)) {
          updatedDescriptions = [descValue, ...descriptions];
          setDescriptions(updatedDescriptions);
          localStorage.setItem(`descriptions_${form.clientId}`, JSON.stringify(updatedDescriptions));
        }
        if (form.clientId && variantValue && !variants.includes(variantValue)) {
          updatedVariants = [variantValue, ...variants];
          setVariants(updatedVariants);
          localStorage.setItem(`variants_${form.clientId}`, JSON.stringify(updatedVariants));
        }
        setForm(initialForm);
        setShowAddStockForm(false); // Close the modal after save
        // Refresh items
        setLoading(true);
        setError(null);
        fetch(`${API_URL}/items`)
          .then((res) => {
            if (!res.ok) throw new Error('Failed to fetch stock items');
            return res.json();
          })
          .then((data) => {
            setItems(data);
            invalidateChartCache(); // Invalidate chart cache when new item added
            setLoading(false);
          })
          .catch((err) => {
            setError(err.message);
            setLoading(false);
          });
      }
    } catch (err) {
      setFormError('Network error: ' + err.message);
    }
    setSubmitting(false);
  };

  // Hide over runs from main stock items and dashboardFilteredItems when overRunsActive
  const filteredItems = overRunsActive
    ? items.filter(i => Number(i.minQty) !== 0)
    : items;
  const filteredDashboardItems = overRunsActive
    ? dashboardFilteredItems.filter(i => Number(i.minQty) !== 0)
    : dashboardFilteredItems;

  // Page navigation state
  const [activePage, setActivePage] = useState('stock');

  // Check if accessing public survey page (before authentication check)
  const isPublicSurvey = window.location.pathname === '/survey';
  
  // If public survey, render it without authentication
  if (isPublicSurvey) {
    return <PublicSurveyPage />;
  }

  // If not authenticated, show login screen only
  if (!currentUser) {
    return (
      <div className="App" style={{
        display: 'flex',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '12px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
          maxWidth: '400px',
          width: '100%',
          textAlign: 'center'
        }}>
          <img src={signsealLogo} alt="SignSeal Logo" style={{
            maxWidth: '200px',
            maxHeight: '80px',
            marginBottom: '20px',
            objectFit: 'contain'
          }} />
          <h1 style={{
            color: '#1976d2',
            marginBottom: '10px',
            fontSize: '28px',
            fontWeight: '900'
          }}>Operations Hub</h1>
          <p style={{
            color: '#666',
            marginBottom: '30px',
            fontSize: '16px'
          }}>Please login to access the application</p>
          
          <button
            onClick={() => setLoginModal({ open: true })}
            style={{
              width: '100%',
              padding: '15px',
              background: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '18px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.background = '#1565c0'}
            onMouseOut={(e) => e.target.style.background = '#1976d2'}
          >
            🔐 Login to Continue
          </button>
        </div>
        
        {/* Login Modal */}
        <LoginModal
          open={loginModal.open}
          onClose={() => setLoginModal({ open: false })}
          onLoginSuccess={handleLoginSuccess}
        />
      </div>
    );
  }

  // If authenticated, show full application
  return (
    <div className="App" style={{display:'flex', minHeight:'100vh'}}>
      {/* Sidebar Navigation */}
      <div style={{width:210,background:'#f8fafc',borderRight:'1px solid #e0e0e0',paddingTop:32,paddingLeft:0,paddingBottom:120,display:'flex',flexDirection:'column',alignItems:'flex-start',position:'fixed',left:0,top:0,height:'100vh',overflowY:'scroll',zIndex:20}}>
        {/* Logo and App Name */}
        <div style={{width:'100%',display:'flex',flexDirection:'column',alignItems:'center',marginBottom:12}}>
          <img src={signsealLogo} alt="SignSeal Logo" style={{maxWidth:170,maxHeight:60,marginBottom:6,marginTop:-6,objectFit:'contain'}} />
          <div style={{fontWeight:900,fontSize:22,letterSpacing:1.2,color:'#1976d2',textAlign:'center',marginBottom:2}}>Operations Hub</div>
        </div>
        
        {/* Main Module Menu - Always Visible */}
        <div style={{width:'100%',paddingLeft:16,paddingRight:16,marginBottom:16}}>
          <div style={{fontSize:12,fontWeight:700,color:'#666',marginBottom:8,textTransform:'uppercase',letterSpacing:0.5}}>Modules</div>
          <button
            onClick={() => setActivePage('stock')}
            style={{
              width:'100%',
              padding:'10px 16px',
              background: activePage !== 'silkscreens' && activePage !== 'silkscreen-reports' && activePage !== 'rawmaterials' && activePage !== 'invoicing' && activePage !== 'surveys' && activePage !== 'production' && activePage !== 'completed-production' && activePage !== 'operators' ? '#1976d2' : '#f5f5f5',
              color: activePage !== 'silkscreens' && activePage !== 'silkscreen-reports' && activePage !== 'rawmaterials' && activePage !== 'invoicing' && activePage !== 'surveys' && activePage !== 'production' && activePage !== 'completed-production' && activePage !== 'operators' ? '#fff' : '#666',
              fontWeight:700,
              border:'none',
              borderRadius:'8px',
              fontSize:14,
              cursor:'pointer',
              transition:'all 0.2s',
              display:'flex',
              alignItems:'center',
              justifyContent:'flex-start',
              gap:'8px',
              marginBottom:8
            }}
            onMouseEnter={(e) => {
              if (activePage === 'silkscreens' || activePage === 'silkscreen-reports' || activePage === 'rawmaterials' || activePage === 'invoicing' || activePage === 'production' || activePage === 'completed-production' || activePage === 'operators') {
                e.target.style.background = '#e0e0e0';
              }
            }}
            onMouseLeave={(e) => {
              if (activePage === 'silkscreens' || activePage === 'silkscreen-reports' || activePage === 'rawmaterials' || activePage === 'invoicing' || activePage === 'production' || activePage === 'completed-production' || activePage === 'operators') {
                e.target.style.background = '#f5f5f5';
              }
            }}
          >
            📦 Stock Management
          </button>
          <button
            onClick={() => setActivePage('silkscreens')}
            style={{
              width:'100%',
              padding:'10px 16px',
              background: activePage === 'silkscreens' || activePage === 'silkscreen-reports' ? '#9C27B0' : '#f5f5f5',
              color: activePage === 'silkscreens' || activePage === 'silkscreen-reports' ? '#fff' : '#666',
              fontWeight:700,
              border:'none',
              borderRadius:'8px',
              fontSize:14,
              cursor:'pointer',
              transition:'all 0.2s',
              display:'flex',
              alignItems:'center',
              justifyContent:'flex-start',
              gap:'8px',
              marginBottom:8
            }}
            onMouseEnter={(e) => {
              if (activePage !== 'silkscreens' && activePage !== 'silkscreen-reports') {
                e.target.style.background = '#e0e0e0';
              }
            }}
            onMouseLeave={(e) => {
              if (activePage !== 'silkscreens' && activePage !== 'silkscreen-reports') {
                e.target.style.background = '#f5f5f5';
              }
            }}
          >
            🖼️ Silkscreens
          </button>
          <button
            onClick={() => setActivePage('rawmaterials')}
            style={{
              width:'100%',
              padding:'10px 16px',
              background: activePage === 'rawmaterials' ? '#FF9800' : '#f5f5f5',
              color: activePage === 'rawmaterials' ? '#fff' : '#666',
              fontWeight:700,
              border:'none',
              borderRadius:'8px',
              fontSize:14,
              cursor:'pointer',
              transition:'all 0.2s',
              display:'flex',
              alignItems:'center',
              justifyContent:'flex-start',
              gap:'8px',
              marginBottom:8
            }}
            onMouseEnter={(e) => {
              if (activePage !== 'rawmaterials') {
                e.target.style.background = '#e0e0e0';
              }
            }}
            onMouseLeave={(e) => {
              if (activePage !== 'rawmaterials') {
                e.target.style.background = '#f5f5f5';
              }
            }}
          >
            🗞️ Raw Materials
          </button>
          <button
            onClick={() => setActivePage('invoicing')}
            style={{
              width:'100%',
              padding:'10px 16px',
              background: activePage === 'invoicing' ? '#4CAF50' : '#f5f5f5',
              color: activePage === 'invoicing' ? '#fff' : '#666',
              fontWeight:700,
              border:'none',
              borderRadius:'8px',
              fontSize:14,
              cursor:'pointer',
              transition:'all 0.2s',
              display:'flex',
              alignItems:'center',
              justifyContent:'flex-start',
              gap:'8px',
              marginBottom:8
            }}
            onMouseEnter={(e) => {
              if (activePage !== 'invoicing') {
                e.target.style.background = '#e0e0e0';
              }
            }}
            onMouseLeave={(e) => {
              if (activePage !== 'invoicing') {
                e.target.style.background = '#f5f5f5';
              }
            }}
          >
            💰 Invoicing
          </button>
          <button
            onClick={() => setActivePage('production')}
            style={{
              width:'100%',
              padding:'10px 16px',
              background: activePage === 'production' ? '#9C27B0' : '#f5f5f5',
              color: activePage === 'production' ? '#fff' : '#666',
              fontWeight:700,
              border:'none',
              borderRadius:'8px',
              fontSize:14,
              cursor:'pointer',
              transition:'all 0.2s',
              display:'flex',
              alignItems:'center',
              gap:8,
              marginBottom:8
            }}
            onMouseEnter={(e) => {
              if (activePage !== 'production') {
                e.target.style.background = '#e0e0e0';
              }
            }}
            onMouseLeave={(e) => {
              if (activePage !== 'production') {
                e.target.style.background = '#f5f5f5';
              }
            }}
          >
            📊 Production
          </button>
          <button
            onClick={() => setActivePage('completed-production')}
            style={{
              width:'100%',
              padding:'10px 16px',
              background: activePage === 'completed-production' ? '#9C27B0' : '#f5f5f5',
              color: activePage === 'completed-production' ? '#fff' : '#666',
              fontWeight:700,
              border:'none',
              borderRadius:'8px',
              fontSize:14,
              cursor:'pointer',
              transition:'all 0.2s',
              display:'flex',
              alignItems:'center',
              gap:8,
              marginBottom:8
            }}
            onMouseEnter={(e) => {
              if (activePage !== 'completed-production') {
                e.target.style.background = '#e0e0e0';
              }
            }}
            onMouseLeave={(e) => {
              if (activePage !== 'completed-production') {
                e.target.style.background = '#f5f5f5';
              }
            }}
          >
            ✅ Completed Production
          </button>
          <button
            onClick={() => setActivePage('operators')}
            style={{
              width:'100%',
              padding:'10px 16px',
              background: activePage === 'operators' ? '#9C27B0' : '#f5f5f5',
              color: activePage === 'operators' ? '#fff' : '#666',
              fontWeight:700,
              border:'none',
              borderRadius:'8px',
              fontSize:14,
              cursor:'pointer',
              transition:'all 0.2s',
              display:'flex',
              alignItems:'center',
              gap:8,
              marginBottom:8
            }}
            onMouseEnter={(e) => {
              if (activePage !== 'operators') {
                e.target.style.background = '#e0e0e0';
              }
            }}
            onMouseLeave={(e) => {
              if (activePage !== 'operators') {
                e.target.style.background = '#f5f5f5';
              }
            }}
          >
            👥 Operators
          </button>
          <button
            onClick={() => setActivePage('surveys')}
            style={{
              width:'100%',
              padding:'10px 16px',
              background: activePage === 'surveys' ? '#FF5722' : '#f5f5f5',
              color: activePage === 'surveys' ? '#fff' : '#666',
              fontWeight:700,
              border:'none',
              borderRadius:'8px',
              fontSize:14,
              cursor:'pointer',
              transition:'all 0.2s',
              display:'flex',
              alignItems:'center',
              justifyContent:'flex-start',
              gap:'8px'
            }}
            onMouseEnter={(e) => {
              if (activePage !== 'surveys') {
                e.target.style.background = '#e0e0e0';
              }
            }}
            onMouseLeave={(e) => {
              if (activePage !== 'surveys') {
                e.target.style.background = '#f5f5f5';
              }
            }}
          >
            📊 Surveys
          </button>
        </div>
        
        {/* Divider */}
        <div style={{width:'80%',height:'1px',background:'#e0e0e0',margin:'0 auto 16px',alignSelf:'center'}}></div>
        {/* Stock Management Section - Hidden when in Silkscreen, Raw Materials, Invoicing, Production, Operators, or Surveys modules */}
        {activePage !== 'silkscreens' && activePage !== 'silkscreen-reports' && activePage !== 'rawmaterials' && activePage !== 'invoicing' && activePage !== 'surveys' && activePage !== 'production' && activePage !== 'completed-production' && activePage !== 'operators' && (
          <>
            <button
              onClick={()=>setActivePage('stock')}
              className={`sidebar-menu-btn${activePage==='stock' ? ' selected' : ''}`}
            >
              <span>📦</span>
              Stock Management
            </button>
            <button
              onClick={() => setActivePage('bookings')}
              className={`sidebar-menu-btn${activePage==='bookings' ? ' selected' : ''}`}
            >
              <span>📋</span>
              Bookings
            </button>
            <button
              onClick={() => setActivePage('backorders')}
              className={`sidebar-menu-btn${activePage==='backorders' ? ' selected' : ''}`}
            >
              <span>⏳</span>
              Backorders
              {activeBackordersCount > 0 && (
                <span style={{
                  marginLeft: 'auto',
                  animation: 'flash 1.5s infinite',
                  fontSize: '16px',
                  color: '#ff6b35'
                }}>⚠️</span>
              )}
            </button>
            <button
              onClick={()=>setActivePage('reports')}
              className={`sidebar-menu-btn${activePage==='reports' ? ' selected' : ''}`}
              title="Customer Stock Analysis now available in Reports"
            >
              <span>📊</span>
              Customer Stock Analysis
            </button>
            <button
              onClick={()=>setActivePage('overruns')}
              className={`sidebar-menu-btn${activePage==='overruns' ? ' selected' : ''}`}
            >
              <span>📊</span>
              Over Runs
            </button>
            <button
              onClick={() => setActivePage('calloffs')}
              className={`sidebar-menu-btn${activePage==='calloffs' ? ' selected' : ''}`}
            >
              <span>📤</span>
              Call-offs
            </button>
            <button
              onClick={() => setActivePage('invoicing')}
              className={`sidebar-menu-btn${activePage==='invoicing' ? ' selected' : ''}`}
            >
              <span>📄</span>
              Invoicing
            </button>
          </>
        )}
        {/* Silkscreen Section - Only visible when in Silkscreen module */}
        {(activePage === 'silkscreens' || activePage === 'silkscreen-reports') && (
          <>
            <button
              onClick={() => setActivePage('silkscreens')}
              className={`sidebar-menu-btn${activePage==='silkscreens' ? ' selected' : ''}`}
            >
              <span>🖼️</span>
              Silkscreens
            </button>
            <button
              onClick={() => setActivePage('silkscreen-reports')}
              className={`sidebar-menu-btn${activePage==='silkscreen-reports' ? ' selected' : ''}`}
            >
              <span>📊</span>
              Silkscreen Reports
            </button>
          </>
        )}
        {/* Stock Management Action Buttons - Hidden when in Silkscreen, Raw Materials, Invoicing, Production, Operators, or Surveys modules */}
        {activePage !== 'silkscreens' && activePage !== 'silkscreen-reports' && activePage !== 'rawmaterials' && activePage !== 'invoicing' && activePage !== 'surveys' && activePage !== 'production' && activePage !== 'completed-production' && activePage !== 'operators' && (
          <>
            <button
              onClick={() => openCheckModal(null, 'in')}
              style={{width:'100%',padding:'12px 0',background:'#43a047',color:'#fff',fontWeight:700,border:'none',borderRadius:'0 20px 20px 0',marginBottom:8,fontSize:16,textAlign:'left',paddingLeft:28,cursor:'pointer',transition:'background 0.15s'}}
            >Check In</button>
            <button
              onClick={() => openCheckModal(null, 'out')}
              style={{width:'100%',padding:'12px 0',background:'#fb8c00',color:'#fff',fontWeight:700,border:'none',borderRadius:'0 20px 20px 0',marginBottom:18,fontSize:16,textAlign:'left',paddingLeft:28,cursor:'pointer',transition:'background 0.15s'}}
            >Check Out</button>
            <button
              onClick={handleOpenAuditLog}
              style={{width:'100%',padding:'12px 0',borderRadius:8,border:'none',fontWeight:700,fontSize:16,background:'#f8fafc',color:'#1976d2',marginBottom:16,cursor:'pointer'}}>
              Audit Log
            </button>
            <button
              onClick={() => setShowAddStockForm(true)}
              className="sidebar-menu-btn"
              style={{background:'#43a047',color:'#fff',fontWeight:700,marginBottom:8}}
            >+ Add Item</button>
            <button
              onClick={() => setActivePage('customers')}
              className={`sidebar-menu-btn${activePage==='customers' ? ' selected' : ''}`}
            >Customer Manager</button>
            
            {/* Divider */}
            <div style={{width:'80%',height:'1px',background:'#e0e0e0',margin:'16px 0'}}></div>
            
            {/* Utility Actions */}
            <button
              onClick={handleExportCSV}
              className="sidebar-menu-btn"
              style={{background:'#f8fafc',color:'#1976d2',fontSize:14}}
            >📊 Export CSV</button>
            <button
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              className="sidebar-menu-btn"
              style={{background:'#f8fafc',color:'#1976d2',fontSize:14}}
            >📥 Import CSV</button>
            <button
              onClick={() => setActivePage('reports')}
              className={`sidebar-menu-btn${activePage==='reports' ? ' selected' : ''}`}
              style={{fontSize:14}}
            >📈 Reports</button>
            <button
              onClick={() => setActivePage('audit')}
              className={`sidebar-menu-btn${activePage==='audit' ? ' selected' : ''}`}
              style={{fontSize:14}}
            >📋 Audit Log</button>
          </>
        )}
        
        {/* Spacer to ensure content overflows viewport */}
        <div style={{height: '60px', flexShrink: 0}}></div>
        
        {/* Authentication UI - positioned at bottom */}
        <div style={{
          width: '100%',
          paddingLeft: 16,
          paddingRight: 16,
          paddingBottom: 32,
          flexShrink: 0
        }}>
          <div style={{
            padding: '8px 16px',
            background: '#e8f5e8',
            color: '#2e7d32',
            borderRadius: 8,
            marginBottom: 12,
            fontSize: 12,
            fontWeight: 600,
            textAlign: 'center'
          }}>
            👤 {currentUser.name}
            <br />
            <span style={{fontSize: 10, opacity: 0.8}}>
              {currentUser.role === 'administrator' ? 'Administrator' : 'User'}
            </span>
          </div>
          {currentUser.role === 'administrator' && (
            <button
              onClick={() => setUserManagementModal({ open: true })}
              style={{
                width: '100%',
                padding: '8px 0',
                background: '#1976d2',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                marginBottom: 8
              }}
            >
              👥 Manage Users
            </button>
          )}
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              padding: '8px 0',
              background: '#d32f2f',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            🚪 Logout
          </button>

        </div>
      </div>
      {/* Main content area */}
      <div style={{flex:1, minWidth:0, marginTop:0, marginLeft:210}}>
        {/* Hero graph and notifications - hidden on Raw Materials, Silkscreens, Invoicing, Production, Operators, and Surveys pages */}
        {activePage !== 'rawmaterials' && activePage !== 'silkscreens' && activePage !== 'silkscreen-reports' && activePage !== 'invoicing' && activePage !== 'surveys' && activePage !== 'production' && activePage !== 'completed-production' && activePage !== 'operators' && (
          <>
            <BelowMinBarChart 
              cacheKey={chartCacheKey}
              onCacheInvalidate={invalidateChartCache}
              overRunsActive={overRunsActive}
            />
            <BookingsNotification bookingsCount={pendingBookingsCount} />
          </>
        )}
        {activePage === 'stock' && (
          <StockManagementPage
            customers={customers}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onItemAction={item => setActionModal({ open: true, item })}
            refreshTrigger={chartCacheKey}
          />
        )}
        {activePage === 'bookings' && (
          <BookingPage 
            items={items} 
            onBookingsChange={count => setPendingBookingsCount(count)}
            onItemsUpdate={() => {
              fetch(`${API_URL}/items`)
                .then(res => res.json())
                .then(data => {
                  const newItems = data.map(item => ({ ...item }));
                  setItems(newItems);
                  invalidateChartCache(); // Invalidate chart cache when booking confirmation affects stock
                })
                .catch(err => console.error('Error refreshing items after booking confirmation:', err));
            }}
          />
        )}
        {activePage === 'backorders' && (
          <BackordersPage 
            customers={customers} 
            onBackorderChange={refreshBackorderCount}
            refreshTrigger={backorderRefreshTrigger}
          />
        )}
        {activePage === 'overruns' && (
          <OverRunsPage
            items={items}
            customers={customers}
            overRunsClientFilter={overRunsClientFilter}
            setOverRunsClientFilter={setOverRunsClientFilter}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onItemAction={item => openCheckModal(item, 'out')}
            setItems={setItems}
          />
        )}
        {activePage === 'customers' && (
          <CustomerManager
            customers={customers}
            items={items}
            onAdd={async (cust) => {
              await fetch(`${API_URL}/clients`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(cust)
              });
              fetch(`${API_URL}/clients`)
                .then(res => res.json())
                .then(setCustomers);
            }}
            onEdit={async (cust) => {
              await fetch(`${process.env.REACT_APP_API_URL || ''}/clients/${cust.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(cust)
              });
              fetch(`${API_URL}/clients`)
                .then(res => res.json())
                .then(setCustomers);
            }}
            onDelete={async (cust) => {
              await fetch(`${process.env.REACT_APP_API_URL || ''}/clients/${cust.id}`, {
                method: 'DELETE'
              });
              fetch(`${API_URL}/clients`)
                .then(res => res.json())
                .then(setCustomers);
            }}
          />
        )}
        {activePage === 'reports' && (
          <ReportsPage />
        )}
        {activePage === 'calloffs' && (
          <CallOffsPage onStockUpdate={() => {
            fetch(`${API_URL}/items`)
              .then(res => res.json())
              .then(data => {
                // Force React to detect change by creating new array with new object references
                const newItems = data.map(item => ({ ...item }));
                setItems(newItems);
                invalidateChartCache(); // Invalidate chart cache when call-off conversion affects stock
              })
              .catch(err => console.error('Error fetching items:', err));
          }} />
        )}
        {activePage === 'invoicing' && (
          <InvoicingPage />
        )}
        {activePage === 'surveys' && (
          <SurveyModule />
        )}
        {activePage === 'rawmaterials' && (
          <RawMaterialsPage />
        )}
        {activePage === 'silkscreens' && (
          <SilkscreensPage />
        )}
        {activePage === 'silkscreen-reports' && (
          <SilkscreenReportsPage />
        )}
        {activePage === 'production' && (
          <ProductionPage />
        )}
        {activePage === 'completed-production' && (
          <CompletedProductionPage />
        )}
        {activePage === 'operators' && (
          <OperatorManager />
        )}
        {activePage === 'audit' && (
          <div style={{padding:40, maxWidth: '1200px', margin: '40px auto'}}>
            <h2 style={{color:'#1976d2',fontWeight:800,fontSize:28,marginBottom:24, textAlign:'center'}}>Audit Log</h2>
            <div style={{background:'#f8fafc',padding:24,borderRadius:12}}>
              <div style={{display:'flex',gap:16,marginBottom:16,alignItems:'center'}}>
                <div>
                  <label>Start Date:</label>
                  <input 
                    type="date" 
                    value={startDate} 
                    onChange={e => setStartDate(e.target.value)}
                    style={{marginLeft:8,padding:'6px 12px',borderRadius:8,border:'1px solid #1976d2'}}
                  />
                </div>
                <div>
                  <label>End Date:</label>
                  <input 
                    type="date" 
                    value={endDate} 
                    onChange={e => setEndDate(e.target.value)}
                    style={{marginLeft:8,padding:'6px 12px',borderRadius:8,border:'1px solid #1976d2'}}
                  />
                </div>
                <button 
                  onClick={() => fetchAuditLog(startDate, endDate)}
                  disabled={!startDate || !endDate}
                  style={{
                    padding:'8px 24px',
                    background: (!startDate || !endDate) ? '#ccc' : '#1976d2',
                    color:'#fff',
                    border:'none',
                    borderRadius:8,
                    fontWeight:700,
                    cursor: (!startDate || !endDate) ? 'not-allowed' : 'pointer'
                  }}
                >
                  Load Audit Log
                </button>
              </div>
              
              {(!startDate || !endDate) ? (
                <div style={{textAlign:'center',padding:40,color:'#666'}}>
                  Please select both start and end dates to view audit log entries.
                </div>
              ) : auditLog.length === 0 ? (
                <div style={{textAlign:'center',padding:40,color:'#666'}}>
                  Click "Load Audit Log" to fetch entries for the selected date range.
                </div>
              ) : (
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse',minWidth:800}}>
                    <thead>
                      <tr style={{background:'#e3eafc'}}>
                        <th style={{padding:'12px',border:'1px solid #d6dbe7',textAlign:'left'}}>Date & Time</th>
                        <th style={{padding:'12px',border:'1px solid #d6dbe7',textAlign:'left'}}>User</th>
                        <th style={{padding:'12px',border:'1px solid #d6dbe7',textAlign:'left'}}>Action</th>
                        <th style={{padding:'12px',border:'1px solid #d6dbe7',textAlign:'left'}}>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLog.map(log => (
                        <tr key={log.id}>
                          <td style={{padding:'8px 12px',border:'1px solid #d6dbe7'}}>
                            {new Date(log.createdAt).toLocaleString()}
                          </td>
                          <td style={{padding:'8px 12px',border:'1px solid #d6dbe7'}}>
                            {log.user ? log.user.name : log.userId || 'System'}
                          </td>
                          <td style={{padding:'8px 12px',border:'1px solid #d6dbe7',fontWeight:600}}>
                            {log.action}
                          </td>
                          <td style={{padding:'8px 12px',border:'1px solid #d6dbe7',maxWidth:400,wordWrap:'break-word'}}>
                            {typeof log.details === 'string' ? log.details : JSON.stringify(log.details)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {/* Modals remain global */}
      {renderEditModal()}
      {renderAuditModal()}
      
      {/* Authentication Modals */}
      <LoginModal
        open={loginModal.open}
        onClose={() => setLoginModal({ open: false })}
        onLoginSuccess={handleLoginSuccess}
      />
      <UserManagement
        open={userManagementModal.open}
        onClose={() => setUserManagementModal({ open: false })}
        currentUser={currentUser}
      />

      <StockItemActionModal
        open={!!actionModal.open}
        item={actionModal.item}
        onClose={() => setActionModal({ open: false, item: null })}
        onCheckIn={item => {
          setActionModal({ open: false, item: null });
          openCheckModal(item, 'in');
        }}
        onCheckOut={item => {
          setActionModal({ open: false, item: null });
          openCheckModal(item, 'out');
        }}
        onBook={item => {
          setActionModal({ open: false, item: null });
          setBookingModal({ open: true, item });
        }}
        onCallOff={item => {
          setActionModal({ open: false, item: null });
          setCallOffModal({ open: true, item });
        }}
      />
      <BookingModal
        open={bookingModal.open}
        item={bookingModal.item}
        onClose={() => setBookingModal({ open: false, item: null })}
        onSuccess={booking => {
          // Immediately update booking count for real-time notification
          setPendingBookingsCount(prevCount => prevCount + 1);
          // Refresh backorder count in case a backorder was created
          refreshBackorderCount();
          // Refetch items after booking to update bookedQty
          fetch(`${API_URL}/items`)
            .then(res => res.json())
            .then(data => {
              // Ensure each item has a unique object reference for React updates
              const newItems = data.map(item => ({ ...item }));
              setItems(newItems);
              invalidateChartCache(); // Invalidate chart cache when booking affects stock
            })
            .catch(err => console.error('Error refreshing items after booking:', err));
        }}
      />

      <CallOffModal
        isOpen={callOffModal.open}
        stockItem={callOffModal.item}
        onClose={() => setCallOffModal({ open: false, item: null })}
        onCallOffCreated={callOff => {
          setCallOffModal({ open: false, item: null });
          alert('Call-off created successfully!');
          fetch(`${API_URL}/items`)
            .then(res => res.json())
            .then(data => {
              setItems(data);
              invalidateChartCache(); // Invalidate chart cache when call-off affects stock
            });
        }}
      />

      <CheckInOutModal
        open={checkModal.open}
        mode={checkModal.mode}
        item={checkModal.item}
        items={items}
        customers={customers}
        onClose={closeCheckModal}
        onSuccess={updated => {
          setItems(items => items.map(i => i.id === updated.id ? updated : i));
          // Refresh backorder count in case backorders were fulfilled during check-in
          refreshBackorderCount();
          closeCheckModal();
        }}
      />
      {showAddStockForm && (
        <AddStockModal
          open={showAddStockForm}
          onClose={() => setShowAddStockForm(false)}
          onSuccess={() => {
            setShowAddStockForm(false);
            fetch(`${API_URL}/items`)
              .then(res => res.json())
              .then(setItems);
          }}
          customers={customers}
          getClientOptions={getClientOptions}
        />
      )}
    </div>
  );
}

export default App;
